import React from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Typography,
  Divider,
  useTheme,
  Paper,
  AppBar,
  Toolbar
} from '@mui/material';
import AppBackground from '../../components/layout/AppBackground';
import {
  Settings as SettingsIcon,
  People as UsersIcon,
  Email as EmailIcon,
  Description as TemplatesIcon,
  Dashboard as DashboardIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

interface MenuItem {
  text: string;
  icon?: React.ReactNode;
  path?: string;
}

interface MenuGroup {
  text: string;
  items: MenuItem[];
}

const AdminPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const drawerWidth = 240;
  const theme = useTheme();
  
  const menuItems: MenuGroup[] = [
    { 
      text: 'System Settings',
      items: [
        { text: 'Lookups', icon: <SettingsIcon />, path: '/utilities/admin/lookups' },
      ]
    },
    {
      text: 'User Management',
      items: [
        { text: 'Users', icon: <UsersIcon />, path: '/utilities/admin/users' },
      ]
    },
    {
      text: 'Communication',
      items: [
        { text: 'Notifications', icon: <NotificationsIcon />, path: '/utilities/admin/notifications' },
        { text: 'Email Config', icon: <EmailIcon />, path: '/utilities/admin/email-config' },
        { text: 'Email Templates', icon: <TemplatesIcon />, path: '/utilities/admin/email-templates' },
        { text: 'Email Logs', icon: <EmailIcon />, path: '/utilities/admin/email-logs' },
      ]
    },
  ];

  return (
    <AppBackground>
      <Toaster position="top-right" />
      <AppBar position="fixed" sx={{
        backgroundColor: 'rgba(15, 23, 42, 0.8)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}>
        <Toolbar>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              width: '100%',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                onClick={() => navigate('/')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    '& .MuiTypography-root': {
                      color: '#3a7bd5'
                    }
                  }
                }}
              >
                <DashboardIcon sx={{ color: '#3a7bd5' }} />
                <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 500 }}>
                  Back to Dashboard
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
              <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>
                TPI Compliance Admin
              </Typography>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      <Toolbar /> {/* This empty Toolbar provides spacing below the AppBar */}
      <Box sx={{ display: 'flex' }}>
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              top: 'auto',
              height: 'calc(100vh - 128px)', // Adjusted for the new AppBar (64px * 2)
              mt: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.95)', // Darker background with higher opacity
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRight: '1px solid rgba(255, 255, 255, 0.1)',
              color: 'white',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
              borderRadius: '0 0 12px 0',
            },
          }}
        >
          <Box sx={{ overflow: 'auto', mt: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box 
              sx={{ 
                px: 3, 
                py: 2,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #3a7bd5, #00d2ff)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                }}
              >
                <SettingsIcon sx={{ fontSize: 24, color: 'white' }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                }}
              >
                Admin Panel
              </Typography>
            </Box>

            <List sx={{ px: 2, flex: 1 }}>
              {menuItems.map((group, index) => (
                <Box key={group.text} sx={{ mb: 3 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="overline"
                      sx={{
                        px: 1,
                        py: 0.5,
                        display: 'block',
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontWeight: 600,
                        letterSpacing: '1px',
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      {group.text}
                    </Typography>
                  </Box>
                  {group.items && (
                    group.items.map((item) => (
                      <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
                        <ListItemButton
                          onClick={() => navigate(item.path || '')}
                          selected={location.pathname === item.path}
                          sx={{
                            borderRadius: 2,
                            py: 1.2,
                            mb: 0.5,
                            transition: 'all 0.2s ease-in-out',
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(58, 123, 213, 0.25)',
                              color: '#fff',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                              borderLeft: '4px solid #3a7bd5',
                              pl: 1.5, // Adjust padding to account for border
                              '&:hover': {
                                backgroundColor: 'rgba(58, 123, 213, 0.3)',
                              },
                              '& .MuiListItemIcon-root': {
                                color: '#3a7bd5',
                              },
                            },
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.08)',
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: location.pathname === item.path
                                ? '#3a7bd5'
                                : 'rgba(255, 255, 255, 0.7)',
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={item.text}
                            primaryTypographyProps={{
                              fontSize: '0.875rem',
                              fontWeight: location.pathname === item.path ? 600 : 400,
                              letterSpacing: '0.2px',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))
                  )}
                </Box>
              ))}
            </List>
            
            <Box 
              sx={{ 
                pt: 3, 
                pb: 2, 
                px: 2, 
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 1
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '0.7rem',
                  textAlign: 'center'
                }}
              >
                TPI Compliance Admin
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: '#10b981', // emerald-500
                  }}
                />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.7rem'
                  }}
                >
                  System Online
                </Typography>
              </Box>
            </Box>
          </Box>
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: `calc(100% - ${drawerWidth}px)`,
            backgroundColor: 'transparent',
            minHeight: `calc(100vh - ${theme.spacing(8)})`,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              backgroundColor: 'rgba(15, 23, 42, 0.7)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              minHeight: `calc(100vh - ${theme.spacing(16)})`,
              color: 'white',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Outlet />
          </Paper>
        </Box>
      </Box>
    </AppBackground>
  );
};

export default AdminPanel;
